import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronLeft, MapPin, Users, CheckCircle2, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/blog/case-study-singapore-360-pax-incentive-trip";
const TITLE = "Case Study: 360-Pax Singapore Incentive Trip Delivered in 60 Days";
const DESCRIPTION =
  "How Trawelldmc operated a 360-pax 4-day Singapore incentive programme on a 60-day lead time — Marina Bay venues, F1 weekend overlap, and 100% retention rate.";
const PUBLISHED_ISO = "2026-02-17";
const PUBLISHED_LABEL = "Feb 17, 2026";
const AUTHOR = "Trawelldmc Team";
const IMAGE =
  "https://images.unsplash.com/photo-1565967511849-76a60a516170?crop=entropy&cs=srgb&fm=webp&w=1600&q=80";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: "Case Study: 360-Pax Singapore Incentive", path: PATH },
];

const ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: TITLE,
  description: DESCRIPTION,
  image: IMAGE,
  datePublished: PUBLISHED_ISO,
  dateModified: PUBLISHED_ISO,
  author: { "@type": "Organization", name: AUTHOR, url: "https://www.trawelldmc.com" },
  publisher: {
    "@type": "Organization",
    name: "Trawelldmc",
    logo: { "@type": "ImageObject", url: "https://www.trawelldmc.com/trawelldmc-logo-hd.png" },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.trawelldmc.com${PATH}` },
};

const KPIS = [
  { label: "Delegates", value: "360" },
  { label: "Lead Time", value: "60 days" },
  { label: "NPS", value: "94/100" },
  { label: "Retention", value: "100%" },
];

export default function CaseStudySingaporeIncentive() {
  return (
    <div data-testid="case-study-singapore-incentive">
      <Seo
        path={PATH}
        title={`${TITLE} | Trawelldmc Case Study`}
        description={DESCRIPTION}
        type="article"
        image={IMAGE}
        breadcrumb={BREADCRUMB}
        extraJsonLd={ARTICLE_JSONLD}
      />

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8">
            <ChevronLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <div className="overline !text-[#FF5238] mb-5">Case Study · Singapore DMC</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08]">
            {TITLE}
          </h1>
          <div className="mt-8 flex items-center gap-5 text-sm text-white/60 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {PUBLISHED_LABEL}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 7 min read</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Singapore</span>
            <span>By {AUTHOR}</span>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 -mt-10 mb-12">
          <img
            src={IMAGE}
            alt="Marina Bay Sands Singapore — Trawelldmc 360-pax incentive case study"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-md shadow-xl"
          />
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 mb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {KPIS.map((k) => (
            <div key={k.label} className="card-flat p-6 text-center">
              <div className="font-heading text-3xl font-light text-[#FF5238]">{k.value}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{k.label}</div>
            </div>
          ))}
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-slate-700 leading-relaxed font-light">
          When a leading Indian insurance brand awarded their President&apos;s
          Club incentive to Singapore with only 60 days of lead time —
          overlapping the Singapore Grand Prix weekend — most DMCs declined.
          Trawelldmc accepted, structured, and delivered a 360-pax 4-day
          programme that returned a 94/100 NPS and earned an automatic renewal
          for 2027.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">01.</span> The Brief
        </h2>
        <p className="text-slate-700 leading-relaxed">
          360 top-performing field agents needed an aspirational reward
          experience. Non-negotiables included a Marina Bay-area 5★ hotel
          block, a gala dinner at an iconic Singapore venue, premium F&amp;B
          throughout, two structured leisure days, and an awards-ceremony
          finale. The dates fell on the F1 weekend — the single most
          challenging Singapore inventory week of the year.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">02.</span> Why It Was Hard
        </h2>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-2 list-disc pl-6">
          <li><strong>Inventory collapse</strong> — Marina Bay hotels routinely sell out 9–12 months ahead of F1; 60-day lead time was a 95% rejection signal across major chains.</li>
          <li><strong>Venue scarcity</strong> — every premium gala venue in Singapore was already pre-booked for sponsor activations.</li>
          <li><strong>Transfer congestion</strong> — F1 weekend road closures around Marina Bay add 40–60 minutes to coach moves.</li>
          <li><strong>Visa documentation</strong> — 360 Indian passport holders in 60 days requires a tightly choreographed appointment cycle.</li>
        </ul>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">03.</span> The Solution
        </h2>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-3 list-none pl-0">
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>JW Marriott South Beach</strong> — a 380-key buyout secured via 19-year senior-team relationship despite the F1 squeeze.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Gardens by the Bay — Flower Dome buyout</strong> for a 360-pax themed gala under the world&apos;s largest glass dome.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Two pre-tested F1 detour routes</strong> for every coach movement — published in the staff briefing pack before delegate arrival.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Visa cell</strong> running 6-days-a-week with our Singapore consulate liaison — every passport cleared 11 days before departure.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Leisure day-2</strong> — Universal Studios + S.E.A. Aquarium private group entry with branded lanyards.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Awards finale</strong> at the Marina Bay Sands SkyPark with LED screens, motivational keynote, and trophy ceremony.</span></li>
        </ul>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">04.</span> The Outcome
        </h2>
        <div className="bg-[#FDFCF8] border-l-4 border-[#FF5238] p-7 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <TrendingUp className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">94/100 NPS</div>
              <div className="text-sm text-slate-600 mt-1">Highest-ever score for the client&apos;s annual incentive</div>
            </div>
            <div>
              <Users className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">100% retention</div>
              <div className="text-sm text-slate-600 mt-1">Every qualifying agent attended — zero drop-outs</div>
            </div>
            <div>
              <CheckCircle2 className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">On-budget</div>
              <div className="text-sm text-slate-600 mt-1">Delivered at 2% under approved budget envelope</div>
            </div>
            <div>
              <Calendar className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">2027 renewed</div>
              <div className="text-sm text-slate-600 mt-1">Automatic award for next year&apos;s 450-pax President&apos;s Club</div>
            </div>
          </div>
        </div>

        <p className="text-slate-700 leading-relaxed mt-8">
          The takeaway for incentive planners: 60-day Singapore programmes are
          deliverable — but only with a DMC whose senior-team relationships
          unlock inventory that public channels can&apos;t. That&apos;s the
          quiet superpower of two decades operating on the ground.
        </p>

        <div className="mt-12 p-8 bg-[#0B192C] text-white rounded-md">
          <div className="overline !text-[#FF5238] mb-3">Brief us</div>
          <h3 className="font-heading text-2xl font-medium tracking-tight">
            Tight timeline. Premium expectations. Big group.
          </h3>
          <p className="text-white/80 mt-3 leading-relaxed">
            Talk to a senior B2B account manager. We respond within 48 hours
            with a costed, themed proposal across any of our 11 Asia DMC
            markets.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/inquiry?destination=Singapore&type=Incentive" className="btn-primary inline-flex">
              Submit an RFP <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/singapore-dmc" className="btn-secondary !bg-white/10 !border-white/20 !text-white hover:!bg-white/20">
              Read our Singapore DMC services
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
